// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { AuthProvider } from './context/AuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import BottomNav from './components/BottomNav';
import NotificationPopup from './components/NotificationPopup';
import WhatsAppButton from './components/WhatsAppButton';
import Home from './pages/Home';
import Shop from './pages/Shop';
import About from './pages/About';
import Contact from './pages/Contact';
import CategoryProducts from './pages/CategoryProducts';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Wishlist from './pages/Wishlist';
import Profile from './pages/Profile';
import Login from './pages/Login';
import Register from './pages/Register';
import Notifications from './pages/Notifications';
import AppDownload from './pages/AppDownload';
import Checkout from './pages/Checkout';
import OrderSuccess from './pages/OrderSuccess';
import OrderTracking from './pages/OrderTracking';
import AdminLogin from './admin/AdminLogin';
import AdminLayout from './admin/AdminLayout';
import Dashboard from './admin/Dashboard';
import Analytics from './admin/Analytics';
import AdminProfile from './admin/AdminProfile';
import BannerManagement from './admin/BannerManagement';
import TimerBannerManagement from './admin/TimerBannerManagement';
import MarqueeManagement from './admin/MarqueeManagement';
import GalleryManagement from './admin/GalleryManagement';
import VideoGalleryManagement from './admin/VideoGalleryManagement';
import AppDownloadManagement from './admin/AppDownloadManagement';
import WhatsAppSettings from './admin/WhatsAppSettings';
import WhatsAppOrderPlugin from './admin/WhatsAppOrderPlugin';
import WhyChooseUsManagement from './admin/WhyChooseUsManagement';
import PageManagement from './admin/PageManagement';
import ContactMessages from './admin/ContactMessages';
import ReviewManagement from './admin/ReviewManagement';
import NotificationManagement from './admin/NotificationManagement';
import CouponManagement from './admin/CouponManagement';
import ShippingTaxSettings from './admin/ShippingTaxSettings';
import Settings from './admin/Settings';
import ProductManagement from './admin/ProductManagement';
import CategoryManagement from './admin/CategoryManagement';
import CustomerManagement from './admin/CustomerManagement';
import OrderManagement from './admin/OrderManagement';
import TeamManagement from './admin/TeamManagement';
import ThemeSettings from './admin/ThemeSettings';
import PluginManagement from './admin/PluginManagement';
import useTheme from './utils/useTheme';
import useScrollReveal from './utils/useScrollReveal';
import { trackVisitor } from './utils/trackVisitor';

const ThemeApplier = () => {
  const { theme } = useTheme();
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(theme).forEach(([key, value]) => {
      const cssVarName = '--color-' + key.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '');
      root.style.setProperty(cssVarName, value);
    });
    document.body.style.backgroundColor = theme.bodyBg;
    document.body.style.color = theme.primaryText;
  }, [theme]);
  return null;
};

const ScrollRevealInit = () => {
  useScrollReveal();
  return null;
};

// ✅ Visitor Tracking Component
const VisitorTracker = () => {
  useEffect(() => {
    // Track visitor on every page load
    trackVisitor(window.location.pathname);

    // Track on route change
    const handleRouteChange = () => {
      trackVisitor(window.location.pathname);
    };

    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  return null;
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <WishlistProvider>
          <Router>
            <ThemeApplier />
            <ScrollRevealInit />
            <VisitorTracker />
            <Routes>

              {/* ADMIN LOGIN */}
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* ADMIN PANEL */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="banners" element={<BannerManagement />} />
                <Route path="timer-banners" element={<TimerBannerManagement />} />
                <Route path="marquee" element={<MarqueeManagement />} />
                <Route path="gallery" element={<GalleryManagement />} />
                <Route path="video-gallery" element={<VideoGalleryManagement />} />
                <Route path="app-download" element={<AppDownloadManagement />} />
                <Route path="whatsapp" element={<WhatsAppSettings />} />
                <Route path="whatsapp-order" element={<WhatsAppOrderPlugin />} />
                <Route path="why-choose-us" element={<WhyChooseUsManagement />} />
                <Route path="pages" element={<PageManagement />} />
                <Route path="contact-messages" element={<ContactMessages />} />
                <Route path="orders" element={<OrderManagement />} />
                <Route path="products" element={<ProductManagement />} />
                <Route path="categories" element={<CategoryManagement />} />
                <Route path="reviews" element={<ReviewManagement />} />
                <Route path="notifications" element={<NotificationManagement />} />
                <Route path="coupons" element={<CouponManagement />} />
                <Route path="shipping-tax" element={<ShippingTaxSettings />} />
                <Route path="customers" element={<CustomerManagement />} />
                <Route path="team" element={<TeamManagement />} />
                <Route path="theme" element={<ThemeSettings />} />
                <Route path="plugins" element={<PluginManagement />} />
                <Route path="settings" element={<Settings />} />
                <Route path="profile" element={<AdminProfile />} />
              </Route>

              {/* CUSTOMER WEBSITE */}
              <Route
                path="/*"
                element={
                  <div className="min-h-screen flex flex-col overflow-x-hidden" style={{ backgroundColor: 'var(--color-body-bg)' }}>
                    <Header />
                    <main className="flex-1 w-full pb-32 md:pb-0">
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/shop" element={<Shop />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/category/:name" element={<CategoryProducts />} />
                        <Route path="/product/:id" element={<ProductDetail />} />
                        <Route path="/cart" element={<Cart />} />
                        <Route path="/checkout" element={<Checkout />} />
                        <Route path="/order-success/:id" element={<OrderSuccess />} />
                        <Route path="/order-tracking/:id" element={<OrderTracking />} />
                        <Route path="/profile/wishlist" element={<Wishlist />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/notifications" element={<Notifications />} />
                        <Route path="/app-download" element={<AppDownload />} />
                      </Routes>
                    </main>
                    <Footer />
                    <BottomNav />
                    <NotificationPopup />
                    <WhatsAppButton />
                  </div>
                }
              />

            </Routes>
          </Router>
        </WishlistProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;